// --------------------------------------------------------------
// Created On: 2021-11-29
// Author: Zachary Thomas
//
// Last Modified: 2025-11-05
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect } from "react";
import Modal from "../Modal/Modal";
import ModalHeader from "../ModalHeader/ModalHeader";
import ModalBody from "../ModalBody/ModalBody";
import ModalFooter from "../ModalFooter/ModalFooter";
import Error from "src/components/Error/Error";
import styles from "./PermanentlyDeleteModal.module.scss";

// Modal for confirming a permanent deletion.
export default function PermanentlyDeleteModal(props: Props): Component {
  const [confirmationText, setConfirmationText] = useState<string>("");
  const [deletionReady, setDeletionReady] = useState<boolean>(false);

  // Check to see if the user is ready to delete.
  useEffect(() => {
    if (confirmationText.toUpperCase() === "PERMANENTLY DELETE") {
      setDeletionReady(true);
    } else {
      setDeletionReady(false);
    }
  }, [confirmationText]);

  // Attempt to delete.
  function attemptDelete(): void {
    if (deletionReady) {
      setConfirmationText("");
      setDeletionReady(false);
      props.onDelete();
    }
  }

  // Cancel delete.
  function cancelDelete(): void {
    setConfirmationText("");
    setDeletionReady(false);
    props.onCancel();
  }

  return (
    <div className="confirm-modal" data-test="confirm-permanent-delete-modal">
      <Modal
        show={props.showModal}
        onHide={() => cancelDelete()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--super-modal-z-index)" }}
        animation
        centered
      >
        <ModalHeader>
          <h5>{props.title}</h5>
        </ModalHeader>
        <ModalBody>
          <p>{props.content}</p>

          <br />

          <p>
            Enter &apos;
            <span className="font-weight-bold">PERMANENTLY DELETE</span>&apos; in the box below to confirm.
          </p>
          <input
            data-test="confirm-permanent-delete-input"
            className="form-control"
            placeholder="PERMANENTLY DELETE"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
          />

          {props.errorMessage !== undefined && props.errorMessage.length > 0 && (
            <div className="mt-3">
              <Error message={props.errorMessage} />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            data-test="confirm-permanent-delete-button"
            type="button"
            className="btn btn-danger"
            data-dismiss="modal"
            onClick={() => attemptDelete()}
            disabled={!deletionReady}
          >
            Delete
          </button>
          <button
            data-test="cancel-permanent-delete-button"
            type="button"
            className="btn btn-secondary"
            onClick={() => cancelDelete()}
          >
            Cancel
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

interface Props {
  showModal: boolean;
  title: string;
  content: string;
  errorMessage?: string;
  onDelete: () => void;
  onCancel: () => void;
}
